/**
 * Types du domaine photo de chantier.
 *
 * Une photo vit à deux endroits : le fichier dans le bucket `photos`, la ligne
 * dans `project_photos`. La ligne porte ce que le fichier ne sait pas dire —
 * à quel chantier il appartient, jusqu'à quand il est conservé, et si le
 * patron en a déjà fait une archive.
 */

export interface ProjectPhoto {
  id: string;
  projectId: string;
  /** Chemin dans le bucket, `tenant_id` en premier segment. */
  storagePath: string;
  takenAt: string;
  /** Date de purge. Calculée en base, jamais saisie ici. */
  expiresAt: string;
  /** Dernière fois que le patron a téléchargé l'archive du chantier. */
  downloadedAt: string | null;
  /**
   * Visible par le client dans son espace de suivi.
   *
   * **Faux par défaut, et c'est délibéré** : une photo de chantier montre
   * parfois autre chose que l'ouvrage — un dégât, une erreur de pose, le
   * jardin du voisin. Le partage est un geste du patron, jamais un état
   * initial.
   */
  sharedWithClient: boolean;
}

/**
 * Étapes d'avertissement, en jours restants avant la purge.
 *
 * Trois, décroissantes : le patron est prévenu un mois avant, puis à quinze
 * jours, puis une semaine avant. La photo n'est supprimée qu'après le
 * troisième — c'est l'engagement que `photo_retention_notices` documente.
 *
 * La tâche planifiée `purge-project-photos` applique les mêmes seuils. Elle ne
 * peut pas importer ce module (Deno, hors du monorepo) : les deux listes
 * doivent être modifiées ensemble.
 */
export const NOTICE_THRESHOLDS_DAYS = [30, 15, 7] as const;

const DAY_MS = 24 * 60 * 60 * 1000;

export interface PhotoRetention {
  /** Jours restants avant purge. Négatif une fois l'échéance dépassée. */
  daysLeft: number;
  /** Vrai dès le premier avertissement : l'écran doit alors le dire. */
  isExpiring: boolean;
  /** L'échéance est passée. La photo attend le prochain passage de la purge. */
  isExpired: boolean;
  /**
   * Rang de l'avertissement en cours, 1 à 3, ou `null` avant le premier. Sert
   * à écrire « dernier rappel » plutôt que « rappel », ce qui n'est pas la
   * même information.
   */
  step: number | null;
}

/**
 * État de conservation d'une échéance donnée.
 *
 * Arrondi vers le bas : à 29,4 jours il reste 29 jours, pas 30. Annoncer plus
 * de temps qu'il n'en reste est la seule erreur qui coûte des photos.
 */
export function photoRetention(expiresAt: string, now: Date): PhotoRetention {
  const deadline = new Date(expiresAt).getTime();
  const daysLeft = Math.floor((deadline - now.getTime()) / DAY_MS);

  const first = NOTICE_THRESHOLDS_DAYS[0];
  const step = daysLeft > first ? null : stepFor(daysLeft);

  return {
    daysLeft,
    isExpiring: daysLeft <= first,
    isExpired: daysLeft < 0,
    step,
  };
}

/**
 * Rang d'avertissement correspondant à un nombre de jours restants.
 *
 * Le dernier seuil franchi l'emporte : à 3 jours de l'échéance on est au
 * troisième rappel, pas au premier.
 */
export function stepFor(daysLeft: number): number {
  let step = 0;
  for (const threshold of NOTICE_THRESHOLDS_DAYS) {
    if (daysLeft <= threshold) step += 1;
  }
  return step;
}

/** La plus proche échéance d'un lot. `null` si le lot est vide. */
export function earliestExpiry(photos: readonly ProjectPhoto[]): string | null {
  let earliest: string | null = null;
  for (const photo of photos) {
    if (earliest === null || photo.expiresAt < earliest) earliest = photo.expiresAt;
  }
  return earliest;
}

/** La plus récente d'abord : c'est l'ordre dans lequel on regarde un chantier. */
export function sortPhotos(photos: readonly ProjectPhoto[]): ProjectPhoto[] {
  return [...photos].sort((a, b) => b.takenAt.localeCompare(a.takenAt));
}

/**
 * Nom du fichier dans l'archive téléchargée.
 *
 * Horodaté, donc trié chronologiquement une fois sur le disque du patron, et
 * préfixé du nom du chantier — une clé USB finit toujours par contenir les
 * photos de plusieurs chantiers.
 */
export function archiveEntryName(
  projectName: string,
  photo: ProjectPhoto,
  index: number,
): string {
  const stamp = photo.takenAt.slice(0, 19).replace(/[:T]/g, "-");
  const ext = photo.storagePath.split(".").pop()?.toLowerCase() ?? "jpg";
  // L'index désambiguïse deux photos prises dans la même seconde, ce qu'une
  // rafale produit sans effort.
  return `${projectSlug(projectName)}/${stamp}-${String(index + 1).padStart(3, "0")}.${ext}`;
}

/** Réduit un nom de chantier à ce qu'un système de fichiers accepte partout. */
export function projectSlug(value: string): string {
  // NFD sépare « é » en « e » + accent ; on retire ensuite la plage des
  // diacritiques combinants. « Réfection toiture » devient « Refection-toiture ».
  const ascii = value.normalize("NFD").replace(/[̀-ͯ]/g, "");
  return (
    ascii
      .replace(/[^A-Za-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "chantier"
  );
}
